"use client";

import { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { PlainMessage } from "@bufbuild/protobuf";
import { EventDetails } from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import { motion, AnimatePresence } from "framer-motion";
import { sidebarVariants } from "./constants";
import { useBoolean, useMediaQuery } from "usehooks-ts";
import { TriggerButton } from "./TriggerButton";
import { useTranslations } from "~/hooks/useTranslations";
import { Section } from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer";
import { FilterContent } from "~/app/tickets/[eventId]/_components/Filter/FilterContent";

interface IFilter {
  sections: Array<PlainMessage<Section>>;
}

export function Filter({ sections }: Readonly<IFilter>): ReactNode {
  const t = useTranslations();
  const { value: isModalOpen, setValue: setModalOpen } = useBoolean(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return isDesktop ? (
    <Dialog.Root open={isModalOpen} onOpenChange={setModalOpen}>
      <Dialog.Trigger asChild>
        <div>
          <TriggerButton onOpen={() => setModalOpen(true)} />
        </div>
      </Dialog.Trigger>
      <AnimatePresence>
        {isModalOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.2 } }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
              />
            </Dialog.Overlay>
            <motion.div
              className="fixed right-0 top-0 z-50 h-full w-full max-w-[350px]"
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Dialog.Content className="h-full bg-white p-5 shadow-lg focus:outline-none">
                <div className="mb-4 flex items-center justify-between">
                  <Dialog.Title className="text-lg font-semibold text-gray-800">
                    {t.filter}
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      onClick={() => setModalOpen(false)}
                      className="text-gray-500 hover:text-gray-800 focus:outline-none"
                      aria-label="Close"
                    >
                      <X className="size-[24px]" />
                    </button>
                  </Dialog.Close>
                </div>
                <FilterContent
                  sections={sections}
                  setModalOpen={setModalOpen}
                />
              </Dialog.Content>
            </motion.div>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  ) : (
    <Drawer open={isModalOpen} onOpenChange={setModalOpen}>
      <DrawerTrigger>
        <div>
          <TriggerButton onOpen={() => setModalOpen(true)} />
        </div>
      </DrawerTrigger>
      <DrawerContent hideBar draggable={false}>
        <DrawerHeader>
          <DrawerTitle>{t.filter}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription className="sr-only">{t.filter}</DrawerDescription>
        <FilterContent sections={sections} setModalOpen={setModalOpen} />
      </DrawerContent>
    </Drawer>
  );
}
